import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useTheme } from '../contexts/ThemeContext';
import { useCompanyLogo } from '../hooks/useCompanyLogo';
import { DatePicker } from '../components/ui/date-picker';
import { CameraIcon } from '@heroicons/react/24/outline';
import { hoyLocal } from '../utils/fechaLocal';

interface Spec { label: string; value: string; }
interface PublicAssetData {
  id: string; code: string; clase: string; area: string; areaLabel: string;
  description: string; marca?: string; modelo?: string; specs: Spec[];
  ubicacionFisica?: string; estado: string; fechaAlta?: string; responsable?: string;
  hasFoto: boolean; fichaDocId?: string | null; nroSerie?: string; proveedor?: string; garantia?: string;
}
interface Maint { id: string; fecha: string; rutina?: string; responsable?: string; observaciones?: string; }

const ESTADO: Record<string, { label: string; cls: string }> = {
  operativo: { label: 'Operativo', cls: 'bg-emerald-500/15 text-emerald-600' },
  en_reparacion: { label: 'En reparación', cls: 'bg-amber-500/15 text-amber-600' },
  de_baja: { label: 'De baja', cls: 'bg-slate-500/15 text-slate-500' },
};
const RUTINAS = ['Rutina A', 'Rutina B', 'Rutina C', 'Servicio', 'Reparación', 'Otro'];

const PublicAsset: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { isDarkMode } = useTheme();
  const { getLogoUrl, getLogoDarkUrl } = useCompanyLogo();
  const [asset, setAsset] = useState<PublicAssetData | null>(null);
  const [maint, setMaint] = useState<Maint[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const isLogged = !!localStorage.getItem('token');
  const loginHref = `/login?returnTo=${encodeURIComponent(window.location.pathname)}`;
  const [form, setForm] = useState({ fecha: hoyLocal(), rutina: 'Rutina A', responsable: '', observaciones: '' });
  const [saving, setSaving] = useState(false);
  const fotoRef = useRef<HTMLInputElement>(null);
  const [fotoUploading, setFotoUploading] = useState(false);
  const [fotoVer, setFotoVer] = useState(0);

  const uploadFoto = async (file: File) => {
    setFotoUploading(true);
    try {
      const fd = new FormData();
      fd.append('foto', file);
      await axios.post(`/api/assets/public/${token}/foto`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Foto subida');
      setFotoVer(v => v + 1);
      await load();
    } catch { toast.error('Error al subir la foto'); }
    finally { setFotoUploading(false); if (fotoRef.current) fotoRef.current.value = ''; }
  };

  const load = async () => {
    try {
      const r = await axios.get(`/api/assets/public/${token}`);
      setAsset(r.data.asset); setMaint(r.data.maintenance || []);
    } catch { setNotFound(true); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const register = async () => {
    setSaving(true);
    try {
      const tk = localStorage.getItem('token');
      await axios.post(`/api/assets/public/${token}/maintenance`, form, { headers: { Authorization: `Bearer ${tk}` } });
      toast.success('Mantenimiento registrado');
      setForm(f => ({ ...f, observaciones: '' }));
      await load();
    } catch (e: any) {
      if (e.response?.status === 401) toast.error('Sesión vencida. Iniciá sesión de nuevo.');
      else toast.error('Error al registrar');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" /></div>;
  if (notFound || !asset) return <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">Equipo no encontrado.</div>;

  const est = ESTADO[asset.estado] || ESTADO.operativo;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-xl mx-auto p-4 space-y-4">
        {/* Cabecera */}
        <div className="rounded-2xl border border-border bg-card p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <img src={isDarkMode ? getLogoDarkUrl() : getLogoUrl()} onError={e => { (e.currentTarget as HTMLImageElement).src = '/rocam-new-logo-2025.png'; }} alt="ROCAM" className="h-6 mb-1 object-contain" />
              <div className="font-mono text-2xl font-extrabold">{asset.code}</div>
              <div className="text-sm mt-0.5">{asset.description}</div>
              {(asset.marca || asset.modelo) && <div className="text-xs text-muted-foreground">{[asset.marca, asset.modelo].filter(Boolean).join(' · ')}</div>}
            </div>
            <span className={`text-xs px-2 py-1 rounded-full font-semibold shrink-0 ${est.cls}`}>{est.label}</span>
          </div>
          {asset.hasFoto && (
            <img src={`/api/assets/public/${token}/photo?v=${fotoVer}`} alt={asset.description} className="mt-3 w-full max-h-60 object-contain rounded-lg bg-muted" />
          )}
          {isLogged ? (
            <>
              <input ref={fotoRef} type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && uploadFoto(e.target.files[0])} />
              <button onClick={() => fotoRef.current?.click()} disabled={fotoUploading} className="mt-2 w-full flex items-center justify-center gap-1.5 rounded-md border border-border py-2 text-sm font-medium hover:bg-muted disabled:opacity-50">
                <CameraIcon className="h-4 w-4" /> {fotoUploading ? 'Subiendo...' : (asset.hasFoto ? 'Cambiar foto' : 'Subir foto del equipo')}
              </button>
            </>
          ) : (
            <a href={loginHref} className="mt-2 w-full flex items-center justify-center gap-1.5 rounded-md border border-border py-2 text-sm font-medium hover:bg-muted">
              <CameraIcon className="h-4 w-4" /> Iniciá sesión para subir la foto
            </a>
          )}
          <div className="grid grid-cols-2 gap-2 mt-3 text-sm">
            <div><span className="text-muted-foreground text-xs">Sector</span><div>{asset.areaLabel}</div></div>
            {asset.ubicacionFisica && <div><span className="text-muted-foreground text-xs">Ubicación</span><div>{asset.ubicacionFisica}</div></div>}
            {asset.fechaAlta && <div><span className="text-muted-foreground text-xs">Alta</span><div>{asset.fechaAlta}</div></div>}
            {asset.responsable && <div><span className="text-muted-foreground text-xs">Responsable</span><div>{asset.responsable}</div></div>}
            {asset.nroSerie && <div><span className="text-muted-foreground text-xs">N° serie</span><div>{asset.nroSerie}</div></div>}
            {asset.garantia && <div><span className="text-muted-foreground text-xs">Garantía</span><div>{asset.garantia}</div></div>}
          </div>
        </div>

        {/* Ficha técnica */}
        {asset.specs && asset.specs.length > 0 && (
          <div className="rounded-2xl border border-border bg-card p-4">
            <div className="text-sm font-semibold mb-2">Ficha técnica</div>
            <div className="divide-y divide-border">
              {asset.specs.map((s, i) => (
                <div key={i} className="flex justify-between gap-3 py-1.5 text-sm">
                  <span className="text-muted-foreground">{s.label}</span>
                  <span className="font-medium text-right">{s.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Registrar mantenimiento (requiere login) */}
        <div className="rounded-2xl border border-border bg-card p-4">
          <div className="text-sm font-semibold mb-2">Registrar mantenimiento</div>
          {isLogged ? (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-muted-foreground">Fecha</label>
                  <DatePicker value={form.fecha} onChange={v => setForm(f => ({ ...f, fecha: v }))} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Rutina</label>
                  <select value={form.rutina} onChange={e => setForm(f => ({ ...f, rutina: e.target.value }))} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                    {RUTINAS.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Responsable</label>
                <input value={form.responsable} onChange={e => setForm(f => ({ ...f, responsable: e.target.value }))} placeholder="Quién lo hizo" className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Observaciones</label>
                <textarea rows={2} value={form.observaciones} onChange={e => setForm(f => ({ ...f, observaciones: e.target.value }))} placeholder="Qué se hizo / hallazgos" className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none" />
              </div>
              <button onClick={register} disabled={saving} className="w-full rounded-md bg-primary text-primary-foreground py-2 text-sm font-medium disabled:opacity-50">
                {saving ? 'Guardando...' : 'Registrar'}
              </button>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">
              Para registrar mantenimiento, <a href={loginHref} className="text-primary underline">iniciá sesión</a> en el sistema.
            </div>
          )}
        </div>

        {/* Historial */}
        <div className="rounded-2xl border border-border bg-card p-4">
          <div className="text-sm font-semibold mb-2">Historial de mantenimiento</div>
          {maint.length === 0 ? <p className="text-sm text-muted-foreground">Sin registros todavía.</p> : (
            <div className="space-y-1.5">
              {maint.map(m => (
                <div key={m.id} className="text-sm border border-border rounded-lg p-2">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{m.fecha}</span>
                    <span className="text-xs px-1.5 py-0.5 rounded bg-muted">{m.rutina || '—'}</span>
                  </div>
                  {m.responsable && <div className="text-xs text-muted-foreground">Por: {m.responsable}</div>}
                  {m.observaciones && <div className="text-xs mt-0.5">{m.observaciones}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicAsset;
